import { StopsellApplyPayload } from '~/types/stopsell';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import {
  SourceBlackoutRange,
  TaggedBlackoutRange,
  collapseWithinSource,
  reconcileBlackoutSchedule,
} from './blackoutReconciliation';
import { blackoutAppliesToRoomType, classifyRoomsScope } from './blackoutScopeClassifier';
import type { ScopeSource } from './blackoutScopeClassifier';
import type { SalesforceBlackoutDate } from './types';

export interface RoomTypeMergePlan {
  roomTypeId: string;
  roomTypeName: string;
  roomRateIds: Array<string>;
  scopeSource: ScopeSource;
  desiredRanges: Array<TaggedBlackoutRange>;
  /** False when neither source applies this round — must be left alone, or reconciling against an empty desired state would delete its whole existing schedule. */
  isTouched: boolean;
}

export interface RoomTypeBlackoutPlan {
  roomTypeId: string;
  roomTypeName: string;
  scopeSource: ScopeSource;
  blocksToDelete: StopsellApplyPayload['rooms'][number]['operations'][number]['blocksToDelete'];
  rangesToCreate: StopsellApplyPayload['rooms'][number]['operations'][number]['rangesToCreate'];
}

function groupRoomRatesByRoomType(
  roomRateSelections: Array<RoomRateSelection>,
): Array<{ roomType: App.RoomType; roomRates: Array<App.RoomRate> }> {
  const roomTypesById = new Map<string, { roomType: App.RoomType; roomRates: Array<App.RoomRate> }>();

  roomRateSelections.forEach(({ roomType, roomRate }) => {
    const group = roomTypesById.get(roomType.id) ?? { roomType, roomRates: [] };
    group.roomRates.push(roomRate);
    roomTypesById.set(roomType.id, group);
  });

  return Array.from(roomTypesById.values());
}

function getRoomTypeScopeSource(
  scopedBlackoutDates: Array<SalesforceBlackoutDate>,
  roomTypeNames: Array<string>,
): ScopeSource {
  if (
    scopedBlackoutDates.some(
      (blackoutDate) => classifyRoomsScope(blackoutDate.name, roomTypeNames).source === 'needs_review',
    )
  ) {
    return 'needs_review';
  }

  if (
    scopedBlackoutDates.some((blackoutDate) => classifyRoomsScope(blackoutDate.name, roomTypeNames).scope === 'all')
  ) {
    return 'inferred_all';
  }

  return 'explicit';
}

export function buildRoomTypeMergePlans(
  blackoutDates: Array<SalesforceBlackoutDate>,
  docRangesByRoomType: Record<string, Array<SourceBlackoutRange>>,
  roomRateSelections: Array<RoomRateSelection>,
  roomTypeNames: Array<string>,
): Array<RoomTypeMergePlan> {
  return groupRoomRatesByRoomType(roomRateSelections).map(({ roomType, roomRates }) => {
    const scopedBlackoutDates = blackoutDates.filter((blackoutDate) =>
      blackoutAppliesToRoomType(blackoutDate.name, roomType.name, roomTypeNames),
    );

    const salesforceRanges: Array<SourceBlackoutRange> = scopedBlackoutDates.map((blackoutDate) => ({
      id: blackoutDate.sfid,
      start: blackoutDate.from__c,
      end: blackoutDate.to__c,
    }));

    const docRanges = docRangesByRoomType[roomType.id] ?? [];
    const desiredRanges = [
      ...collapseWithinSource(salesforceRanges, 'salesforce'),
      ...collapseWithinSource(docRanges, 'stopsell_doc'),
    ];

    return {
      roomTypeId: roomType.id,
      roomTypeName: roomType.name,
      roomRateIds: roomRates.map((roomRate) => roomRate.id),
      scopeSource: getRoomTypeScopeSource(scopedBlackoutDates, roomTypeNames),
      desiredRanges,
      isTouched: scopedBlackoutDates.length > 0 || docRanges.length > 0,
    };
  });
}

export function buildRoomTypeApplyPlans(
  mergePlans: Array<RoomTypeMergePlan>,
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>,
): Array<RoomTypeBlackoutPlan> {
  return mergePlans.map((plan) => {
    if (!plan.isTouched) {
      return {
        roomTypeId: plan.roomTypeId,
        roomTypeName: plan.roomTypeName,
        scopeSource: plan.scopeSource,
        blocksToDelete: [],
        rangesToCreate: [],
      };
    }

    const perRateResults = plan.roomRateIds.map((roomRateId) =>
      reconcileBlackoutSchedule(roomRateId, plan.desiredRanges, blackoutDatesByRoomRate[roomRateId] ?? []),
    );

    return {
      roomTypeId: plan.roomTypeId,
      roomTypeName: plan.roomTypeName,
      scopeSource: plan.scopeSource,
      blocksToDelete: perRateResults.flatMap((result) => result.blocksToDelete),
      rangesToCreate: perRateResults.flatMap((result) => result.rangesToCreate),
    };
  });
}

export function buildBlackoutApplyPayload(
  plans: Array<RoomTypeBlackoutPlan>,
  includedRoomTypeIds: Set<string>,
): StopsellApplyPayload {
  return {
    rooms: plans
      .filter(
        (plan) =>
          includedRoomTypeIds.has(plan.roomTypeId) &&
          (plan.blocksToDelete.length > 0 || plan.rangesToCreate.length > 0),
      )
      .map((plan) => ({
        roomTypeId: plan.roomTypeId,
        operations: [
          { operation: 'blackout' as const, blocksToDelete: plan.blocksToDelete, rangesToCreate: plan.rangesToCreate },
        ],
      })),
  };
}
